import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import Selector from "./Selector";
import PropTypes from "prop-types";
import { FONT_FAMILY, BORDER_RADIUS } from "../styles/variables";
import { toIntlFormattedValue, getNumberString } from "../utils/numberParsers";
import ThemeContext from "../utils/theme";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";

export const useStyles = makeStyles({
  root: {
    display: "flex",
    fontFamily: FONT_FAMILY,
    marginTop: (props) => props.spacingXl,
    "& label.Mui-focused": {
      color: (props) => `rgba(${props.colorTextBase}, 1)`,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "red",
    },
    "& .MuiFormLabel-root, & .MuiCheckbox-root": {
      color: (props) => `rgba(${props.colorTextBase}, .54)`,
    },
    "& .MuiCheckbox-root.MuiCheckbox-colorSecondary.Mui-checked": {
      color: (props) => `rgba(${props.colorPrimary}, 1)`,
    },
    "& .MuiCheckbox-root.MuiCheckbox-colorSecondary:hover": {
      backgroundColor: (props) => `rgba(${props.colorPrimary}, .1)`,
    },
    "& .MuiFormControlLabel-label": {
      color: ({ colorTextBase }) => `rgba(${colorTextBase}, 1)`,
    },
    "& .MuiFormControlLabel-label a": {
      color: "inherit",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: BORDER_RADIUS,
      color: (props) => `rgba(${props.colorTextBase}, 1)`,
      "& fieldset": {
        borderColor: (props) => `rgba(${props.colorTextBase}, 0.1)`,
      },
      "&:hover fieldset": {
        borderColor: (props) => `rgba(${props.colorTextBase}, 0.3)`,
      },
      "&.Mui-focused fieldset": {
        borderWidth: "1px",
        borderColor: (props) => `rgba(${props.colorTextBase}, 0.6)`,
      },
      "&.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.1)",
        "& fielset": {
          borderColor: "rgba(0, 0, 0, .1)",
        },
      },
    },
  },
});

/**
 * Styled FieldText component from Material UI library
 * https://material-ui.com/components/text-fields/
 */
export default function Input({
  className,
  type,
  onChange,
  label,
  htmlLabel,
  csvData,
  ...restProps
}) {
  const { colorPrimary, colorTextBase, spacingXl } = React.useContext(ThemeContext);
  const classes = useStyles({ colorTextBase, spacingXl, colorPrimary });

  if (process.env.NODE_ENV !== "production") {
    // process.env.NODE_ENV is static
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (htmlLabel && type !== "checkbox") {
        console.warn(
          `htmlLabel is not compatible with type: ${type}, only with type checkbox.`,
        );
      }
    }, [htmlLabel, type]);
  }

  const value = type === "checkbox" ? !!restProps.value : restProps.value;

  const inputMode = type === "number" ? "numeric" : "text";

  const handleChange = (event) => {
    if (inputMode === "numeric") {
      const numberLikeValue = getNumberString(event.target.value);

      if (Number.isNaN(Number(numberLikeValue))) {
        return;
      }

      const intlValue = toIntlFormattedValue(numberLikeValue);

      // losing target's information useful to formik
      const touchedEvent = {
        ...event,
        target: {
          ...event.target,
          value: intlValue,
        },
      };

      onChange(touchedEvent);
    } else {
      onChange(event);
    }
  };

  const inputProps = {
    inputMode,
  };

  const getComponent = (ctype) => {
    switch (ctype) {
      case "checkbox":
        return (
          <span>
            <FormControlLabel
              className={classes.root}
              control={
                <Checkbox
                  {...restProps}
                  value={value}
                  checked={value}
                  onChange={handleChange}
                  variant="outlined"
                />
              }
              label={
                htmlLabel ? (
                  <span dangerouslySetInnerHTML={{ __html: htmlLabel }} />
                ) : (
                  label
                )
              }
            />
            {restProps.error && (
              <FormHelperText error={true}>{restProps.helperText}</FormHelperText>
            )}
          </span>
        );
      case "selector":
        return (
          <Selector
            {...restProps}
            label={label}
            csvData={csvData}
            onChange={handleChange}
            classes={classes.root}
            variant="outlined"
          />
        );
      default:
        return (
          <TextField
            {...restProps}
            label={label}
            type={type}
            onChange={handleChange}
            className={classes.root}
            variant="outlined"
            inputProps={inputProps}
          />
        );
    }
  };

  return getComponent(type);
}

Input.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  htmlLabel: PropTypes.string,
  id: PropTypes.string.isRequired,
  csvData: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.oneOf(["text", "email", "number", "checkbox", "selector"]),
  onChange: PropTypes.func.isRequired,
};
